import { CancelProps, CancelStages } from './cancel.types';
import { Confirmation, Summary } from './components';
import React, { useCallback, useMemo } from 'react';

import { ModalIds } from '../../../../../../contexts/modals/modals.types';
import { useModalsContext } from '../../../../../../contexts';

/**
 * useCancelData hook
 * @description The hook which processes Order Cancel flow data
 *
 * @author Oleksii Medvediev
 * @category Hooks
 */
const useCancelData = () => {
  const {
    data: { orderCancel },
    dispatch,
  } = useModalsContext();

  const flowData: CancelProps | undefined = useMemo(() => orderCancel, [orderCancel]);

  const setStage = useCallback(
    (stage: CancelProps['currentStage']) =>
      flowData &&
      dispatch({
        type: 'UPDATE_MODAL_DATA',
        payload: {
          id: ModalIds.orderCancel,
          data: {
            [ModalIds.orderCancel]: { ...flowData, currentStage: stage },
          },
        },
      }),
    [dispatch, flowData],
  );

  const submitHandlers: Record<CancelProps['currentStage'], () => void> = useMemo(
    () => ({
      [CancelStages.summary]: () => {
        setStage(CancelStages.confirmation);
      },
      [CancelStages.confirmation]: async () => {
        flowData?.onSubmitCancel && flowData.onSubmitCancel();
      },
    }),
    [flowData, setStage],
  );

  const stages: Record<CancelProps['currentStage'], JSX.Element> | undefined = useMemo(
    () =>
      flowData
        ? {
            [CancelStages.summary]: (
              <Summary
                onClose={() => dispatch({ type: 'HIDE_MODAL', payload: ModalIds.orderCancel })}
                onSubmit={submitHandlers.summary}
                summaryRecommendations={flowData.recommendations.map(
                  ({ account, cardDescription, id, order, orderDescription, symbol }) => ({
                    account,
                    cardDescription,
                    id,
                    order,
                    orderDescription,
                    symbol,
                  }),
                )}
              />
            ),
            [CancelStages.confirmation]: (
              <Confirmation
                onClose={() => {
                  dispatch({ type: 'HIDE_MODAL', payload: ModalIds.orderCancel });
                }}
                onSubmit={submitHandlers.confirmation}
              />
            ),
          }
        : undefined,
    [dispatch, flowData, submitHandlers.confirmation, submitHandlers.summary],
  );

  return {
    stages,
    currentStage: useMemo(() => flowData?.currentStage, [flowData?.currentStage]),
  };
};

export { useCancelData };
